// Mieter-Widgets und Bereiche (Handoff Mieter §5.3, §5.4, §6). Ein Klick öffnet ?panel=<key>.

import {
  AddressBook, ClockCounterClockwise, CurrencyEur, DeviceMobile, FolderSimple, Gauge, Key, NotePencil, Signature, Sparkle, SquaresFour,
  Ticket, CalendarBlank, Vault, Wallet, type Icon,
} from '@phosphor-icons/react';
import type { MieterGruppe } from '../../lib/mieter/detail';
import { PostLogo } from '../epost/PostLogo';
import type { WidgetGroesse } from './ui/Widget';

export type MieterPanelKey =
  | 'konto' | 'miete' | 'mv' | 'kaution' | 'kontakt' | 'portal' | 'aktivitaet'
  | 'vertraege' | 'dokumente' | 'briefe' | 'termine' | 'tickets' | 'zaehler' | 'notizen' | 'ki';

export interface MieterWidgetDef { key: MieterPanelKey; gruppe: MieterGruppe; groesse: WidgetGroesse }

/** Reihenfolge und Größen laut README §5.3. */
export const MIETER_WIDGETS: readonly MieterWidgetDef[] = [
  { key: 'konto', gruppe: 'fin', groesse: 'gross' },
  { key: 'miete', gruppe: 'fin', groesse: 'breit' },
  { key: 'mv', gruppe: 'verm', groesse: 'normal' },
  { key: 'kaution', gruppe: 'fin', groesse: 'normal' },
  { key: 'kontakt', gruppe: 'komm', groesse: 'breit' },
  { key: 'portal', gruppe: 'komm', groesse: 'normal' },
  { key: 'aktivitaet', gruppe: 'komm', groesse: 'breit' },
  { key: 'vertraege', gruppe: 'verm', groesse: 'normal' },
  { key: 'dokumente', gruppe: 'verm', groesse: 'normal' },
  { key: 'briefe', gruppe: 'komm', groesse: 'normal' },
  { key: 'termine', gruppe: 'haus', groesse: 'normal' },
  { key: 'tickets', gruppe: 'haus', groesse: 'normal' },
  { key: 'zaehler', gruppe: 'haus', groesse: 'normal' },
  { key: 'notizen', gruppe: 'komm', groesse: 'normal' },
  { key: 'ki', gruppe: 'komm', groesse: 'breit' },
];

/** Widget-Titel und Panel-Kopf (Prototyp PT). */
export const MIETER_PANEL_KOPF: Record<MieterPanelKey, { widget: string; titel: string; icon: Icon }> = {
  konto: { widget: 'Mietkonto', titel: 'Mietkonto', icon: Wallet },
  miete: { widget: 'Gesamtmiete', titel: 'Gesamtmiete', icon: CurrencyEur },
  mv: { widget: 'Mietverhältnis', titel: 'Mietverhältnis', icon: Key },
  kaution: { widget: 'Kaution', titel: 'Kaution', icon: Vault },
  kontakt: { widget: 'Kontakt', titel: 'Stammdaten & Kontakt', icon: AddressBook },
  portal: { widget: 'Mieterportal', titel: 'Mieterportal', icon: DeviceMobile },
  aktivitaet: { widget: 'Aktivität', titel: 'Aktivität', icon: ClockCounterClockwise },
  vertraege: { widget: 'Verträge', titel: 'Verträge', icon: Signature },
  dokumente: { widget: 'Dokumente', titel: 'Dokumente', icon: FolderSimple },
  briefe: { widget: 'Briefe', titel: 'Briefe per Post', icon: PostLogo },
  termine: { widget: 'Termine', titel: 'Termine', icon: CalendarBlank },
  tickets: { widget: 'Tickets', titel: 'Tickets', icon: Ticket },
  zaehler: { widget: 'Zähler', titel: 'Zähler', icon: Gauge },
  notizen: { widget: 'Notizen', titel: 'Notizen', icon: NotePencil },
  ki: { widget: 'KI-Zusammenfassung', titel: 'KI-Zusammenfassung', icon: Sparkle },
};

export function istMieterPanel(v: string | null): v is MieterPanelKey {
  return v !== null && Object.prototype.hasOwnProperty.call(MIETER_PANEL_KOPF, v);
}

/** Seitenleiste im Mietermodus (README §5.4). 'uebersicht' = ohne Panel. */
export const MIETER_NAV: readonly { abschnitt: string; eintraege: readonly { key: MieterPanelKey | 'uebersicht'; label: string; icon: Icon }[] }[] = [
  { abschnitt: 'Mieter', eintraege: [
    { key: 'uebersicht', label: 'Übersicht', icon: SquaresFour },
    { key: 'kontakt', label: 'Stammdaten', icon: AddressBook },
    { key: 'mv', label: 'Mietverhältnis', icon: Key },
    { key: 'portal', label: 'Mieterportal', icon: DeviceMobile },
  ] },
  { abschnitt: 'Finanzen', eintraege: [
    { key: 'konto', label: 'Mietkonto', icon: Wallet },
    { key: 'miete', label: 'Miete', icon: CurrencyEur },
    { key: 'kaution', label: 'Kaution', icon: Vault },
  ] },
  { abschnitt: 'Verwaltung', eintraege: [
    { key: 'vertraege', label: 'Verträge', icon: Signature },
    { key: 'dokumente', label: 'Dokumente', icon: FolderSimple },
    { key: 'briefe', label: 'Briefe', icon: PostLogo },
    { key: 'tickets', label: 'Tickets', icon: Ticket },
    { key: 'termine', label: 'Termine', icon: CalendarBlank },
    { key: 'zaehler', label: 'Zähler', icon: Gauge },
    { key: 'aktivitaet', label: 'Aktivität', icon: ClockCounterClockwise },
    { key: 'notizen', label: 'Notizen', icon: NotePencil },
  ] },
];
